/* eslint-disable */
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import moment from "moment";
import "moment-timezone";
import { PageOrientation, PageSize } from "pdfmake/interfaces";
import { formatCurrency } from "./moneyFormat";
import { logoImage } from "../resources/logos/yoshibase64";

// Solo usar las fuentes por defecto de pdfMake
pdfMake.vfs = pdfFonts.vfs;

// Configurar las fuentes usando la URL base actual
const baseUrl = window.location.origin;

pdfMake.fonts = {
  Inter: {
    normal: `${baseUrl}/fonts/Inter-Regular.otf`,
    bold: `${baseUrl}/fonts/Inter-Bold.otf`,
    italics: `${baseUrl}/fonts/Inter-Thin.otf`,
    bolditalics: `${baseUrl}/fonts/Inter-ExtraLight.otf`,
  },
};

// Configuración por defecto para documentos PDF
const defaultPDFStyle = {
  font: "Inter", // Cambiar temporalmente a Roboto para probar
};

// Ahora puedes usar 'Inter' o 'CamphorPro' en tus documentos
pdfMake.tableLayouts = {
  defaultLayout: {
    hLineWidth: (i: number, node: any) => {
      // No top border above a table whose first header row is a subtitle
      return i === 0 && node.table.headerRows > 1 ? 0 : 0.1;
    },
    vLineColor: () => {
      return "#e5e7eb";
    },
    vLineWidth: (i: number, node: any) => {
      // Solo pinta la primera y última línea vertical (bordes externos)
      return i === 0 || i === node.table.widths.length ? 0.1 : 0;
    },
    hLineColor: () => {
      return "#e5e7eb";
    },
    paddingLeft: () => {
      return 0;
    },
    paddingRight: () => {
      return 0;
    },
  },
  customLayout: {
    fillColor: () => {
      return "#f9fafb";
    },
    hLineWidth: () => {
      return 0.1;
    },
    vLineColor: () => {
      return "#e5e7eb";
    },
    vLineWidth: (i: number, node: any) => {
      // Solo pinta la primera y última línea vertical (bordes externos)
      return i === 0 || i === node.table.widths.length ? 0.1 : 0;
    },
    hLineColor: () => {
      return "#e5e7eb";
    },
  },
  cardLayout: {
    hLineWidth: (i: number, node: any) => {
      // Solo pinta la primera y última línea horizontal (bordes superior e inferior)
      return i === 0 || i === node.table.body.length ? 0.1 : 0;
    },
    vLineColor: () => {
      return "#e5e7eb";
    },
    vLineWidth: (i: number, node: any) => {
      // Solo pinta la primera y última línea vertical (bordes izquierdo y derecho)
      return i === 0 || i === node.table.widths.length ? 0.1 : 0;
    },
    hLineColor: () => {
      return "#e5e7eb";
    },
    paddingLeft: () => {
      return 0;
    },
    paddingRight: () => {
      return 0;
    },
  },
};

// Función auxiliar para procesar datos de una tabla individual
const processTableData = (
  tableConfig: any,
  fontSize: number,
  pageWidth: number,
): any[] => {
  const rows: any[] = [];
  const widths: number[] = [];

  // Insert columns header
  let hasFooter = false;
  const headers: any = [];
  const totalWeight = tableConfig.columns.reduce(
    (acc: any, col: any) => (acc += col.weight),
    0,
  );

  const widthWeight = pageWidth / totalWeight + 1;

  tableConfig.columns.forEach((column: any) => {
    widths.push(column.weight * widthWeight);
    headers.push({
      text: column.name,
      fillColor: "#f9fafb",
      color: "#6b7280",
      fontSize,
      alignment: column.colorType ? "left" : getColumnAlignment(column.type),
      bold: true,
      margin: [5, 5, 5, 5],
    });
    if (column.footer !== undefined) {
      hasFooter = true;
    }
  });

  rows.push({
    table: { widths, body: [headers] },
    layout: "defaultLayout",
  });

  tableConfig.values.forEach((attrs: { [x: string]: any }) => {
    const tableRow: {
      text: any;
      color: string;
      fontSize: number;
      alignment: string;
      bold: boolean;
      margin: number[];
    }[] = [];
    tableConfig.columns.forEach(
      (column: { type: string; value: string | number; colorType: any }) => {
        tableRow.push({
          text: getFormattedValue(column.type, attrs[column.value]),
          color: attrs.color || "#000000", // Usar el color del renglón si existe, sino negro por defecto
          fontSize,
          alignment: column.colorType
            ? "left"
            : getColumnAlignment(column.type),
          bold: false,
          margin: [5, 5, 5, 5],
        });
      },
    );
    rows.push({
      table: { widths, body: [tableRow] },
      layout: "defaultLayout",
    });
  });

  if (hasFooter) {
    const tableFooter: {
      text: any;
      color: string;
      fontSize: number;
      alignment: string;
      bold: boolean;
      margin: number[];
      fillColor: string;
    }[] = [];
    tableConfig.columns.forEach((column: { type: string; colorType: any }) => {
      tableFooter.push({
        text: getFormattedValue(
          column.type,
          getFooterValue(column, tableConfig.values),
        ),
        color: "#000000",
        fillColor: "#f9fafb",
        fontSize,
        alignment: column.colorType ? "left" : getColumnAlignment(column.type),
        bold: true,
        margin: [5, 5, 5, 5],
      });
    });
    rows.push({
      table: { widths, body: [tableFooter] },
      layout: "defaultLayout",
    });
  }

  return rows;
};

const processTemplate = (
  template: any[],
  fontSize: number,
  pageWidth: number,
  filtersWidth = 130,
): any[] => {
  const rows: any[] = [];
  // Column widths of the current table. Reassigned per table (never mutated in place) so each table
  // keeps its own array and standalone "footer" rows can reference the previous table's widths.
  let widths: number[] = [];

  template.forEach((row: any, index: number) => {
    switch (row.type) {
      case "two-columns":
        // Calcular el ancho disponible para cada columna (igual que side-by-side-tables)
        const twoColumnsTotalWidth = pageWidth - 72; // 72 son los márgenes totales de la página
        const twoColumnsSpace = 13; // Espacio pequeño entre las columnas
        const twoColumnsWidth = (twoColumnsTotalWidth - twoColumnsSpace) / 2; // Ancho para cada columna

        rows.push({
          table: {
            widths: [twoColumnsWidth, twoColumnsWidth], // Ancho específico para cada columna
            body: [
              [
                {
                  text: row.leftColumn.value,
                  fontSize: 10,
                  alignment: "left",
                  bold: row.leftColumn.bold || false,
                  color: row.leftColumn.color || "#000000",
                  margin: [5, 5, 5, 5],
                },
                {
                  text: row.rightColumn.value,
                  fontSize: 10,
                  alignment: "right",
                  bold: row.rightColumn.bold || false,
                  color: row.rightColumn.color || "#000000",
                  margin: [5, 5, 5, 5],
                },
              ],
            ],
          },
          layout: "customLayout", // Sin bordes para que se vea como columnas
        });
        break;
      case "side-by-side-tables":
        // Calcular el ancho disponible para cada tabla (casi todo el ancho de página, solo un pequeño espacio entre tablas)
        const totalAvailableWidth = pageWidth - 72; // 72 son los márgenes totales de la página
        const spaceBetweenTables = 15; // Espacio pequeño entre las tablas
        const availableWidth = (totalAvailableWidth - spaceBetweenTables) / 2; // Ancho para cada tabla

        // Procesar la primera tabla con el ancho calculado
        const leftTableRows = processTableData(
          row.leftTable,
          fontSize,
          availableWidth,
        );
        // Procesar la segunda tabla con el ancho calculado
        const rightTableRows = processTableData(
          row.rightTable,
          fontSize,
          availableWidth,
        );

        // Crear una fila con dos columnas, cada una conteniendo una tabla
        rows.push({
          columns: [
            {
              width: availableWidth,
              stack: leftTableRows,
            },
            {
              width: spaceBetweenTables,
              text: "", // Espacio pequeño entre las tablas
            },
            {
              width: availableWidth,
              stack: rightTableRows,
            },
          ],
        });
        break;
      case "title":
        rows.push({
          stack: [
            // Primera fila: título y logo
            {
              columns: [
                {
                  text: row.value,
                  fontSize: fontSize + 8,
                  alignment: "left",
                  bold: true,
                  color: "#000000",
                  margin: [0, 0, 0, 0],
                },
                {
                  width: "auto",
                  text: "",
                },
                {
                  margin: [0, -6, 0, 0],
                  image: logoImage,
                  width: 70,
                  height: 40,
                },
              ],
            },
            // Segunda fila: solo la fecha (completamente independiente)
            {
              text: `Fecha de creación: ${moment
                .utc()
                .locale("es")
                .tz("America/Mexico_City")
                .format("DD MMM, YYYY HH:mm")} hrs (Centro de México)`,
              fontSize: fontSize - 1,
              alignment: "left",
              bold: true,
              color: "#6b7280",
              margin: [2, -15, 0, 10],
            },
          ],
        });
        break;
      case "summary-cards":
        // Crear tarjetas de resumen con n número de rectángulos
        const summaryCardsTotalWidth = pageWidth - 68; // 72 son los márgenes totales de la página
        const totalSpacing = (row.cards.length - 1) * 15; // Espacio total entre tarjetas
        const cardWidth =
          (summaryCardsTotalWidth - totalSpacing) / row.cards.length; // Ancho de cada tarjeta
        const cardHeight = 50; // Altura fija para todas las tarjetas

        const cardRow = {
          columns: [
            // Primera tarjeta (pegada al margen izquierdo)
            {
              width: cardWidth,
              height: cardHeight,
              stack: [
                {
                  table: {
                    widths: [cardWidth],
                    body: [
                      [
                        {
                          text: row.cards[0].value,
                          fontSize: fontSize, // Reducido de +4 a +2
                          alignment: "left",
                          bold: true,
                          color: row.cards[0].valueColor || "#000000",
                          margin: [10, 10, 10, 5],
                          fillColor: "#ffffff", // Fondo blanco fijo para todas
                        },
                      ],
                      [
                        {
                          text: row.cards[0].label,
                          fontSize: fontSize - 1, // Reducido de fontSize a fontSize - 1
                          alignment: "left",
                          bold: false,
                          color: row.cards[0].labelColor || "#6b7280",
                          margin: [10, 0, 10, 10],
                          fillColor: "#ffffff", // Fondo blanco fijo para todas
                        },
                      ],
                    ],
                  },
                  layout: "cardLayout",
                },
              ],
            },
            // Espacio entre primera y segunda tarjeta
            {
              width: 15,
              text: "",
            },
            // Tarjetas intermedias
            ...row.cards
              .slice(1, -1)
              .map((card: any, index: number) => [
                {
                  width: cardWidth,
                  height: cardHeight,
                  stack: [
                    {
                      table: {
                        widths: [cardWidth],
                        body: [
                          [
                            {
                              text: card.value,
                              fontSize: fontSize, // Reducido de +4 a +2
                              alignment: "left",
                              bold: true,
                              color: card.valueColor || "#000000",
                              margin: [10, 10, 10, 5],
                              fillColor: "#ffffff", // Fondo blanco fijo para todas
                            },
                          ],
                          [
                            {
                              text: card.label,
                              fontSize: fontSize - 1, // Reducido de fontSize a fontSize - 1
                              alignment: "left",
                              bold: false,
                              color: card.labelColor || "#6b7280",
                              margin: [10, 0, 10, 10],
                              fillColor: "#ffffff", // Fondo blanco fijo para todas
                            },
                          ],
                        ],
                      },
                      layout: "cardLayout",
                    },
                  ],
                },
                {
                  width: 15,
                  text: "",
                },
              ])
              .flat(),
            // Última tarjeta (pegada al margen derecho)
            {
              width: cardWidth,
              height: cardHeight,
              stack: [
                {
                  table: {
                    widths: [cardWidth],
                    body: [
                      [
                        {
                          text: row.cards[row.cards.length - 1].value,
                          fontSize: fontSize, // Reducido de +4 a +2
                          alignment: "left",
                          bold: true,
                          color:
                            row.cards[row.cards.length - 1].valueColor ||
                            "#000000",
                          margin: [10, 10, 10, 5],
                          fillColor: "#ffffff", // Fondo blanco fijo para todas
                        },
                      ],
                      [
                        {
                          text: row.cards[row.cards.length - 1].label,
                          fontSize: fontSize - 1, // Reducido de fontSize a fontSize - 1
                          alignment: "left",
                          bold: false,
                          color:
                            row.cards[row.cards.length - 1].labelColor ||
                            "#6b7280",
                          margin: [10, 0, 10, 10],
                          fillColor: "#ffffff", // Fondo blanco fijo para todas
                        },
                      ],
                    ],
                  },
                  layout: "cardLayout",
                },
              ],
            },
          ],
        };

        rows.push(cardRow);
        break;
      case "subtitle":
        // A subtitle right before a table is rendered by that table as a repeating header row
        if (
          template[index + 1]?.type === "table" &&
          template[index + 1].columns.length > 0
        ) {
          break;
        }
        rows.push({
          text: row.value,
          fontSize: fontSize + 1,
          alignment: "left",
          bold: true,
          color: "#000000",
          margin: [0, 5, 0, 5],
        });
        break;
      case "text":
        rows.push({
          text: row.value,
          fontSize,
          alignment: "left",
          bold: true,
          color: "#000000",
          margin: [5, 5, 5, 5],
        });
        break;
      case "front-page-text":
        // Special handling for YOSHI logo
        if (row.value === "YOSHI") {
          rows.push({
            image: logoImage,
            width: 100,
            height: 60,
            alignment: "center",
            margin: [0, 20, 0, 20],
          });
        } else {
          rows.push({
            text: row.value,
            fontSize: row.fontSize || fontSize,
            alignment: row.alignment || "center",
            bold: row.bold !== undefined ? row.bold : false,
            color: row.color || "#000000",
            margin: [0, 5, 0, 5],
          });
        }
        break;
      case "table":
        // Insert columns header
        let hasFooter = false;
        const headers: any = [];
        const totalWeight = row.columns.reduce(
          (acc: any, row: any) => (acc += row.weight),
          0,
        );

        const widthWeight = (pageWidth - 72) / totalWeight + 1;

        // Fresh array per table: mutating a shared one in place clipped every table after the first.
        widths = [];

        row.columns.forEach((column: any) => {
          widths.push(column.weight * widthWeight);
          headers.push({
            text: column.name,
            fillColor: "#f9fafb",
            color: "#6b7280",
            fontSize,
            alignment: column.colorType
              ? "left"
              : getColumnAlignment(column.type),
            bold: true,
            margin: [5, 5, 5, 5],
          });
          if (column.footer !== undefined) {
            hasFooter = true;
          }
        });
        // Header, rows and footer share one table so the headers repeat on every page
        const subtitle =
          template[index - 1]?.type === "subtitle" && row.columns.length > 0
            ? template[index - 1]
            : null;
        const noBorder = [false, false, false, false];
        const body: any[] = [];
        if (subtitle) {
          body.push([
            {
              text: subtitle.value,
              colSpan: row.columns.length,
              fontSize: fontSize + 1,
              alignment: "left",
              bold: true,
              color: "#000000",
              // With defaultLayout's 2pt padding it matches the loose subtitle's [0, 5, 0, 5]
              margin: [0, 3, 0, 3],
              border: noBorder,
            },
            ...row.columns.slice(1).map(() => ({ text: "", border: noBorder })),
          ]);
        }
        body.push(headers);
        const headerRows = body.length;
        row.values.forEach((attrs: { [x: string]: any }) => {
          // Check if this row should be styled as a footer
          const isFooterRow = attrs._isFooter || false;
          // A detail row breaks its parent down, so it reads smaller and greyed out
          const isDetailRow = attrs._isDetail || false;
          // Opt-in text color by column value, read only by plain cells (not stacked or image)
          const cellColors: { [columnValue: string]: string } =
            attrs._cellColors || {};
          // Bold digits are wider: a footer at full size wraps its amounts onto a second line
          const rowFontSize = isDetailRow
            ? fontSize - 2
            : isFooterRow
              ? fontSize - 1
              : fontSize;
          const tableRow: any[] = []; // Cambiar el tipo a any[] para permitir stack
          row.columns.forEach(
            (column: {
              type: string;
              value: string | number;
              colorType: any;
              secondaryValue?: string;
              secondaryValueType?: string;
              isFirstValueSubtitle?: boolean;
              isSecondaryValueSubtitle?: boolean;
              footerFieldKey?: string;
              imageWidth?: number;
              imageHeight?: number;
            }) => {
              // Image columns hold a PNG/JPEG data URI: render it as a thumbnail, or leave the cell
              // empty when the value is missing or is not a usable data URI.
              if (column.type === "image") {
                const imageValue = attrs[column.value];
                const cellStyle: any = isImageDataUri(imageValue)
                  ? {
                      image: imageValue,
                      width: column.imageWidth || DEFAULT_IMAGE_SIZE,
                      height: column.imageHeight || DEFAULT_IMAGE_SIZE,
                      alignment: getColumnAlignment(column.type),
                      margin: [5, 5, 5, 5],
                    }
                  : {
                      text: "",
                      fontSize: rowFontSize,
                      margin: [5, 5, 5, 5],
                    };

                // Add footer styling
                if (isFooterRow) {
                  cellStyle.fillColor = "#f9fafb";
                }

                tableRow.push(cellStyle);
                return;
              }

              // Si la columna tiene secondaryValue, agregar el texto secundario
              if (column.secondaryValue) {
                const cellStyle: any = {
                  stack: [
                    {
                      text: getFormattedValue(column.type, attrs[column.value]),
                      fontSize: column.isFirstValueSubtitle
                        ? fontSize - 2
                        : rowFontSize,
                      color:
                        column.isFirstValueSubtitle || isDetailRow
                          ? "#6b7280"
                          : "#000000",
                      bold: isFooterRow,
                    },
                    {
                      text: getFormattedValue(
                        column.secondaryValueType || "text-left",
                        attrs[column.secondaryValue],
                      ),
                      fontSize: column.isSecondaryValueSubtitle
                        ? fontSize - 2
                        : fontSize,
                      color: column.isSecondaryValueSubtitle
                        ? "#6b7280"
                        : "#000000",
                      bold: isFooterRow,
                    },
                  ],
                  alignment: column.colorType
                    ? "left"
                    : getColumnAlignment(column.type),
                  margin: [5, 5, 5, 5],
                };

                // Add footer styling
                if (isFooterRow) {
                  cellStyle.fillColor = "#f9fafb";
                }

                tableRow.push(cellStyle);
              } else {
                const cellStyle: any = {
                  text: getFormattedValue(column.type, attrs[column.value]),
                  color:
                    cellColors[column.value] ??
                    (column.isFirstValueSubtitle || isDetailRow
                      ? "#6b7280"
                      : "#000000"),
                  fontSize: column.isFirstValueSubtitle
                    ? fontSize - 2
                    : rowFontSize,
                  alignment: column.colorType
                    ? "left"
                    : getColumnAlignment(column.type),
                  bold: isFooterRow,
                  margin: [5, 5, 5, 5],
                };

                // Add footer styling
                if (isFooterRow) {
                  cellStyle.fillColor = "#f9fafb";
                }

                tableRow.push(cellStyle);
              }
            },
          );
          body.push(tableRow);
        });
        if (hasFooter) {
          const tableFooter: {
            text: any;
            color: string;
            fontSize: number;
            alignment: string;
            bold: boolean;
            margin: number[];
            fillColor: string;
          }[] = [];
          row.columns.forEach(
            (column: {
              type: string;
              colorType: any;
              footerFieldKey?: string;
              value: string;
              secondaryValue?: string;
              secondaryValueType?: string;
            }) => {
              const footerValue = getFooterValue(
                column,
                row.values,
                column.footerFieldKey,
              );

              // Determinar el tipo de formato para la alineación
              const formatType =
                column.footerFieldKey === "second"
                  ? column.secondaryValueType || "text-left"
                  : column.type;

              tableFooter.push({
                text: footerValue, // Ya viene formateado de getFooterValue
                color: "#000000",
                fillColor: "#f9fafb",
                fontSize,
                alignment: column.colorType
                  ? "left"
                  : getColumnAlignment(formatType),
                bold: true,
                margin: [5, 5, 5, 5],
              });
            },
          );
          body.push(tableFooter);
        }
        // pdfmake silently drops an unbreakable row taller than a page: let those rows break
        const hasTallRow = body
          .slice(headerRows)
          .some(
            (cells) =>
              estimateRowHeight(cells, widths) > MAX_UNBROKEN_ROW_HEIGHT,
          );
        rows.push({
          table: {
            widths,
            headerRows,
            keepWithHeaderRows: hasTallRow ? 0 : 1,
            dontBreakRows: !hasTallRow,
            body,
          },
          layout: "defaultLayout",
        });
        break;
      case "footer":
        const tableRow: {
          text: any;
          fillColor: string;
          color: string;
          fontSize: number;
          alignment: string;
          bold: boolean;
          margin: number[];
        }[] = [];
        row.values.forEach(
          (val: { type: string; value: any; colorType: any }) => {
            tableRow.push({
              text: getFormattedValue(val.type, val.value),
              fillColor: "#f9fafb",
              color: "#000000",
              fontSize,
              alignment: val.colorType ? "left" : getColumnAlignment(val.type),
              bold: true,
              margin: [5, 5, 5, 5],
            });
          },
        );
        rows.push({
          table: { widths, body: [tableRow] },
          layout: "defaultLayout",
        });
        break;
      case "filter":
        // Agregar título antes de los filtros
        rows.push({
          text: "FILTROS APLICADOS",
          fontSize: fontSize,
          alignment: "left",
          bold: true,
          color: "#000000",
          margin: [0, 10, 0, 10],
        });

        // Procesar filtros en grupos de máximo 4 por fila
        const filtersPerRow = 4;
        const totalFilters = row.values.length;
        const totalRows = Math.ceil(totalFilters / filtersPerRow);

        for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
          const startIndex = rowIndex * filtersPerRow;
          const endIndex = Math.min(startIndex + filtersPerRow, totalFilters);
          const currentRowFilters = row.values.slice(startIndex, endIndex);

          // Calcular anchos para esta fila
          const filterWidths: number[] = [];
          const filterRows: any[] = [];
          let maxFilterRows = 0;

          currentRowFilters.forEach((val: { items: string | any[] }) => {
            filterWidths.push(filtersWidth);
            filterRows.push(val.items.length);
            maxFilterRows =
              maxFilterRows < val.items.length
                ? val.items.length
                : maxFilterRows;
          });
          maxFilterRows += 1;

          // Crear las filas de datos para esta fila de filtros
          for (let y = 0; y < maxFilterRows; y++) {
            const tableRow: {
              text: any;
              fontSize: number;
              alignment: string;
              bold: boolean;
              color: string;
            }[] = [];
            let x = 0;

            currentRowFilters.forEach((val: { header: any; items: any[] }) => {
              if (y === 0) {
                tableRow.push({
                  text: val.header,
                  fontSize: fontSize - 1,
                  alignment: "left",
                  bold: true,
                  color: "#6b7280",
                });
              } else if (y - 1 < filterRows[x]) {
                tableRow.push({
                  text: val.items[y - 1],
                  fontSize: fontSize - 2,
                  alignment: "left",
                  bold: false,
                  color: "#6b7280",
                });
              } else {
                tableRow.push({
                  text: "",
                  fontSize,
                  alignment: "left",
                  bold: false,
                  color: "#000000",
                });
              }
              x++;
            });

            rows.push({
              table: { widths: filterWidths, body: [tableRow] },
              layout: "noBorders",
            });
          }
        }
        break;
      default:
        break;
    }
  });

  return rows;
};

export const getPDFFromTemplate = async (
  name: string,
  template: any[],
  pageSize = "LETTER",
  fontSize = 8,
  pageOrientation?: PageOrientation,
  filtersWidth?: number,
  getFile = false,
) => {
  const pageWidth = pageOrientation === "landscape" ? 842 : 612; // letter portrait width
  const rows = processTemplate(template, fontSize, pageWidth, filtersWidth);

  const pdfDoc = pdfMake.createPdf({
    pageOrientation: pageOrientation || "portrait",
    pageMargins: [36, 36, 36, 36],
    pageSize: pageSize as PageSize,
    content: [...rows],
    defaultStyle: {
      ...defaultPDFStyle,
      font: "Inter",
      fontSize: fontSize,
    },
  });

  if (getFile) {
    return new Promise<File>((resolve) => {
      pdfDoc.getBlob((blob: Blob) => {
        const file = new File([blob], name.replace(/ /g, "_") + ".pdf", {
          type: "application/pdf",
        });
        resolve(file);
      });
    });
  } else {
    pdfDoc.download(name.replace(/ /g, "_"));
  }
};

export const createPDFFromRows = async (
  name: string,
  rows: any,
  props: any,
) => {
  pdfMake
    .createPdf({
      pageOrientation: "portrait",
      pageMargins: [22, 22, 22, 22],
      pageSize: "LETTER",
      content: [...rows],
      ...props,
    })
    .download(name.replace(/ /g, "_"));
};

// Default thumbnail size (in pt) used by columns of type "image".
const DEFAULT_IMAGE_SIZE = 20;

// Tallest estimated row (in pt) kept unbroken; pdfMake drops unbreakable blocks over a page. Leaves
// room for repeated headers and for the estimate running up to ~1.8x short on wide glyphs.
const MAX_UNBROKEN_ROW_HEIGHT = 350;

// Rough row height estimate (generous glyph width, lines wrapped by character count) used to spot
// rows that could outgrow a page.
const estimateRowHeight = (cells: any[], widths: number[]): number =>
  Math.max(
    ...cells.map((cell, index) =>
      (cell.stack || [cell]).reduce((height: number, part: any) => {
        if (part.image) {
          return height + part.height;
        }
        const charsPerLine = Math.max(
          1,
          Math.floor((widths[index] - 10) / (part.fontSize * 0.7)),
        );
        const lines = String(part.text ?? "")
          .split("\n")
          .reduce(
            (acc, line) =>
              acc + Math.max(1, Math.ceil(line.length / charsPerLine)),
            0,
          );
        return height + lines * part.fontSize * 1.25;
      }, 10),
    ),
  );

// pdfMake can only embed PNG/JPEG data URIs; any other value has to be skipped so the document
// generation never fails because of an image.
const isImageDataUri = (value: any): boolean =>
  typeof value === "string" && /^data:image\/(png|jpeg);base64,/.test(value);

const getColumnAlignment = (type: string) => {
  switch (type) {
    case "text-left":
      return "left";
    case "text-center":
      return "center";
    case "text-right":
      return "right";
    case "percent":
      return "right";
    case "number":
      return "right";
    case "money":
      return "right";
    case "image":
      return "center";
    default:
      return "left";
  }
};

const getFormattedValue = (type: string, value: any) => {
  switch (type) {
    case "percent":
      return `${Number(value || 0).toFixed(2)}%`;
    case "number":
      return value?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    case "money":
      return typeof value === "string" ? "" : formatCurrency(value);
    default:
      return value;
  }
};

const getFooterValue = (column: any, values: any, footerFieldKey?: string) => {
  // Determinar qué campo usar para la suma
  const fieldToUse =
    footerFieldKey === "second" ? column.secondaryValue : column.value;

  switch (column.type) {
    case "text-first":
    case "text-center":
    case "text-last":
    case "text-left":
    case "text-right":
      if (column.footer === "footerValue") {
        const [last] = values.slice(-1);
        return last.footerValue ? last.footerValue : "";
      }
      if (column.footer === "sum") {
        // Para columnas de texto que tienen "sum", calcular la suma del campo especificado
        const sumValue = values.reduce((acc: any, value: any) => {
          const fieldValue = value[fieldToUse];
          return (
            acc +
            (typeof fieldValue === "number"
              ? fieldValue
              : parseFloat(fieldValue) || 0)
          );
        }, 0);
        // Formatear según el tipo correcto
        const formatType =
          footerFieldKey === "second"
            ? column.secondaryValueType || "text-left"
            : column.type;
        return getFormattedValue(formatType, sumValue);
      }
      return column.footer;
    case "money":
    case "percent":
    case "number":
      switch (column.footer) {
        case "sum":
          const sumValue = values.reduce((acc: any, value: any) => {
            // Usar el campo correcto según footerFieldKey
            const fieldValue = value[fieldToUse];
            return (
              acc +
              (typeof fieldValue === "number"
                ? fieldValue
                : parseFloat(fieldValue) || 0)
            );
          }, 0);
          // Round to 3 decimal places to avoid floating point errors
          const roundedSumValue = Math.round(sumValue * 1000) / 1000;
          // Formatear según el tipo correcto
          const formatType =
            footerFieldKey === "second"
              ? column.secondaryValueType || "text-left"
              : column.type;
          return getFormattedValue(formatType, roundedSumValue);
        case "avg":
          const avgValue =
            values.reduce((acc: any, value: any) => {
              const fieldValue = value[fieldToUse];
              return (
                acc +
                (typeof fieldValue === "number"
                  ? fieldValue
                  : parseFloat(fieldValue) || 0)
              );
            }, 0) / values.length;
          // Formatear según el tipo correcto
          const type =
            footerFieldKey === "second"
              ? column.secondaryValueType || "text-left"
              : column.type;
          return getFormattedValue(type, avgValue);

        case "customValue":
          if (column.customValue1 && column.customValue2)
            return `${values.reduce(
              (acc: any, row: any) =>
                (acc += parseFloat(row[column.customValue1])),
              0,
            )} / ${values.reduce(
              (acc: any, row: any) =>
                (acc += parseFloat(row[column.customValue2])),
              0,
            )} `;
          else return "";
        case "footerValue":
          const [last] = values.slice(-1);
          return last.footerValue ? last.footerValue : "";
        default:
          return "-";
      }
    default:
      return column.footer;
  }
};

export const getMultiPagePDFFromTemplate = async (
  sheets: Array<{
    name: string;
    template: any[];
    pageOrientation?: "portrait" | "landscape";
    pageSize?: string;
    fontSize?: number;
  }>,
  getFile = false,
): Promise<File | void> => {
  const content: any[] = [];

  sheets.forEach((sheetConfig, index) => {
    const fontSize = sheetConfig.fontSize || 8;
    const pageWidth = sheetConfig.pageOrientation === "landscape" ? 842 : 612;

    const rows = processTemplate(sheetConfig.template, fontSize, pageWidth);
    content.push(...rows);

    if (index < sheets.length - 1) {
      content.push({ text: "", pageBreak: "after" });
    }
  });

  const pdfDoc = pdfMake.createPdf({
    pageOrientation: "portrait",
    pageMargins: [36, 36, 36, 36],
    pageSize: "LETTER",
    content: content,
    defaultStyle: {
      font: "Inter",
    },
  });

  if (getFile) {
    return new Promise<File>((resolve) => {
      pdfDoc.getBlob((blob: Blob) => {
        const fileName = sheets[0].name.replace(/ /g, "_") + ".pdf";
        const file = new File([blob], fileName, { type: "application/pdf" });
        resolve(file);
      });
    });
  } else {
    pdfDoc.download(sheets[0].name.replace(/ /g, "_"));
    return Promise.resolve();
  }
};
